import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { ChevronDown } from "lucide-react";
interface ServiceOption {
  title: string;
  description: string;
  details: string[] | null;
  project_type: string | null;
}

const categories = [
  { key: "Website Design", label: "Website Design" },
  { key: "Email Marketing", label: "Email Marketing" },
  { key: "Auditing", label: "Auditing" },
];

interface StepYourProjectProps {
  projectType: string;
  updateProjectType: (value: string) => void;
}

const StepYourProject = ({
  projectType,
  updateProjectType,
}: StepYourProjectProps) => {
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      const { data } = await supabase
        .from("services")
        .select("title, description, details, project_type")
        .order("display_order", { ascending: true });
      if (data) setServices(data as ServiceOption[]);
    };
    fetchServices();
  }, []);

  // Group services by project_type from DB
  const servicesByCategory = useMemo(() => {
    const grouped: Record<string, ServiceOption[]> = {};
    services.forEach((s) => {
      const cat = s.project_type || "Website Design";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(s);
    });
    return grouped;
  }, [services]);

  const selectedService = useMemo(
    () => services.find((s) => s.title === projectType),
    [services, projectType]
  );

  const handleCategorySelect = (key: string) => {
    const next = activeCategory === key ? "" : key;
    setActiveCategory(next);
    if (next && projectType) {
      const current = selectedService?.project_type || "Website Design";
      if (current !== next) updateProjectType("");
    }
    if (!next) {
      updateProjectType("");
      setDropdownOpen(false);
    }
  };

  const handleServiceSelect = (title: string) => {
    updateProjectType(title);
    setDropdownOpen(false);
  };

  // Auto-select category when projectType is pre-set
  useEffect(() => {
    if (projectType && !activeCategory && services.length > 0) {
      const match = services.find((s) => s.title === projectType);
      if (match) setActiveCategory(match.project_type || "Website Design");
    }
  }, [projectType, activeCategory, services]);

  const availableServices = activeCategory ? (servicesByCategory[activeCategory] || []) : [];

  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <div>
        <p className="font-heading text-ts-base text-foreground mb-1">
          Your project
        </p>
        <p className="font-body text-ts-xs text-foreground/60">
          What are you looking for?
        </p>
      </div>

      {/* Category pills */}
      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
          Project type
        </label>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((cat) => (
            <button
              key={cat.key}
              type="button"
              onClick={() => handleCategorySelect(cat.key)}
              className={`px-3 py-1.5 border font-body text-ts-2xs tracking-wide transition-all duration-200 ${
                activeCategory === cat.key
                  ? "bg-primary text-primary-foreground border-primary scale-[1.03]"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Service dropdown */}
      <AnimatePresence>
        {activeCategory && availableServices.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0, overflow: "hidden" }}
            animate={{ opacity: 1, height: "auto", overflow: "visible" }}
            exit={{ opacity: 0, height: 0, overflow: "hidden" }}
            transition={{ duration: 0.25 }}
          >
            <div>
              <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
                Service
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    if (!dropdownOpen) updateProjectType("");
                    setDropdownOpen(!dropdownOpen);
                  }}
                  className={`w-full text-left p-3 border flex items-center justify-between transition-colors ${
                    projectType
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-foreground/30"
                  }`}
                >
                  <span className={`font-body text-ts-xs ${projectType ? "text-foreground font-medium" : "text-foreground/40"}`}>
                    {projectType || "Select a service…"}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`text-muted-foreground transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute z-10 w-full mt-1 border border-border bg-background shadow-lg"
                    >
                      {availableServices.map((service) => (
                        <button
                          key={service.title}
                          type="button"
                          onClick={() => handleServiceSelect(service.title)}
                          className={`w-full text-left px-3 py-2.5 font-body text-ts-xs transition-colors ${
                            projectType === service.title
                              ? "bg-primary/10 text-foreground font-medium"
                              : "text-foreground/70 hover:bg-muted hover:text-foreground"
                          }`}
                        >
                          {service.title}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected service description */}
      <AnimatePresence>
        {selectedService && !dropdownOpen && (
          <motion.div
            key={selectedService.title}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="border-l-2 border-border pl-3 py-1"
          >
            <p className="font-body text-[11px] text-foreground/60 leading-relaxed">
              {selectedService.description}
            </p>
            {selectedService.details && selectedService.details.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {selectedService.details.map((detail) => (
                  <li
                    key={detail}
                    className="font-body text-[11px] text-foreground/40 leading-relaxed"
                  >
                    • {detail}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default StepYourProject;